"use client";

/**
 * App shell theme provider (Paper / Ink).
 *
 * - Applies `data-theme` on <html> (CSS variables live in globals.css).
 * - Hydrates from localStorage, then reconciles with reader_prefs.appTheme
 *   so the choice syncs cross-device via the existing whole-doc put pattern.
 * - Does NOT touch reader text themes (reader/themes.ts).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  applyAppThemeToDocument,
  DEFAULT_APP_THEME,
  normalizeAppTheme,
  readStoredAppTheme,
  resolveAppTheme,
  writeStoredAppTheme,
  type AppThemeId,
} from "@/lib/app-theme";
import { getReaderPrefs, putReaderPrefs } from "@/components/lumi-rust/reader/api";
import {
  DEFAULT_READER_PREFS,
  normalizeReaderPrefs,
  type ReaderPrefs,
} from "@/components/lumi-rust/reader/types";

interface AppThemeContextValue {
  theme: AppThemeId;
  setTheme: (theme: AppThemeId) => void;
  toggleTheme: () => void;
}

const AppThemeContext = createContext<AppThemeContextValue | null>(null);

export interface AppThemeProviderProps {
  children: ReactNode;
  /** Persist to reader_prefs (default true). Pass false for logged-out previews. */
  persist?: boolean;
}

export function AppThemeProvider({
  children,
  persist = true,
}: AppThemeProviderProps) {
  // Start with default; boot script already painted the correct data-theme.
  // Hydrate from storage on mount to sync React state without a flash.
  const [theme, setThemeState] = useState<AppThemeId>(DEFAULT_APP_THEME);
  // Cache the rest of the prefs blob so putting appTheme doesn't clobber
  // reader settings (whole-document replace).
  const otherPrefs = useRef<ReaderPrefs>(DEFAULT_READER_PREFS);
  const hydrated = useRef(false);

  useEffect(() => {
    const stored = readStoredAppTheme();
    const prefersDark =
      typeof window !== "undefined"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
        : null;
    const initial = resolveAppTheme({ stored, prefersDark });
    setThemeState(initial);
    applyAppThemeToDocument(initial);
    hydrated.current = true;

    if (!persist) return;

    let cancelled = false;
    getReaderPrefs()
      .then((raw) => {
        if (cancelled) return;
        const prefs = normalizeReaderPrefs(raw);
        otherPrefs.current = prefs;
        const remote = normalizeAppTheme(prefs.appTheme);
        if (remote) {
          setThemeState(remote);
          writeStoredAppTheme(remote);
          applyAppThemeToDocument(remote);
        } else if (stored) {
          // Local has a choice but server doesn't yet — push it up.
          const next = { ...prefs, appTheme: stored };
          otherPrefs.current = next;
          putReaderPrefs(next).catch(() => {});
        }
      })
      .catch(() => {
        // logged out / offline — local value already applied
      });

    return () => {
      cancelled = true;
    };
  }, [persist]);

  // Keep DOM in sync if theme changes after hydration.
  useEffect(() => {
    if (!hydrated.current) return;
    applyAppThemeToDocument(theme);
  }, [theme]);

  const setTheme = useCallback(
    (next: AppThemeId) => {
      const resolved = normalizeAppTheme(next) ?? DEFAULT_APP_THEME;
      setThemeState(resolved);
      writeStoredAppTheme(resolved);
      applyAppThemeToDocument(resolved);

      if (persist) {
        const merged = { ...otherPrefs.current, appTheme: resolved };
        otherPrefs.current = merged;
        putReaderPrefs(merged).catch(() => {
          // best-effort; localStorage keeps the choice on this device
        });
      }
    },
    [persist],
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === "paper" ? "ink" : "paper");
  }, [theme, setTheme]);

  const value = useMemo<AppThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <AppThemeContext.Provider value={value}>{children}</AppThemeContext.Provider>
  );
}

export function useAppTheme(): AppThemeContextValue {
  const ctx = useContext(AppThemeContext);
  if (!ctx) {
    throw new Error("useAppTheme must be used within an AppThemeProvider");
  }
  return ctx;
}
